<template>
  <div>
    <div v-if="modules.length < 1" class="mod-empty">
      No available modules.
    </div>
    <div v-else class="mod-grid">
      <template v-for="(module, index) of modules">
        <!-- Divider -->
        <div v-if="module.type == 'divider'" :key="'div-' + index" class="mod-divider"></div>

        <!-- External link card -->
        <a
          v-else-if="module.href"
          :key="'href-' + index"
          :href="module.disabled || (module.isAllowed !== undefined && !module.isAllowed) ? null : module.href"
          target="_blank"
          class="mod-card-link"
          :class="{ 'mod-card-link--disabled': module.disabled || (module.isAllowed !== undefined && !module.isAllowed) }"
        >
          <div class="mod-card">
            <div class="mod-card__content">
              <div class="mod-card__top">
                <div class="mod-card__icon" :style="{ background: getColor(index) + '14', color: getColor(index) }">
                  <v-icon :color="getColor(index)" size="22">{{ module.icon || 'mdi-open-in-new' }}</v-icon>
                </div>
                <div class="mod-card__info">
                  <h3 class="mod-card__name">{{ module.name }}</h3>
                  <p class="mod-card__desc">Opens in new tab</p>
                </div>
              </div>
              <div class="mod-card__bottom">
                <span class="mod-card__status mod-card__status--pill mod-card__status--external">
                  <v-icon size="12" color="var(--app-text-muted)" class="mr-1">mdi-open-in-new</v-icon>
                  External
                </span>
                <v-icon size="16" class="mod-card__go" color="#c0c7cf">mdi-arrow-right</v-icon>
              </div>
            </div>
          </div>
        </a>

        <!-- Route card -->
        <router-link
          v-else-if="module.route"
          :key="'route-' + index"
          :to="module.disabled || (module.isAllowed !== undefined && !module.isAllowed) ? '' : module.route"
          :event="module.disabled || (module.isAllowed !== undefined && !module.isAllowed) ? '' : 'click'"
          class="mod-card-link"
          :class="{
            'mod-card-link--disabled': module.disabled || (module.isAllowed !== undefined && !module.isAllowed),
            'mod-card-link--hidden': module.isAllowed === false,
          }"
        >
          <div class="mod-card">
            <div class="mod-card__content">
              <div class="mod-card__top">
                <div class="mod-card__icon" :style="{ background: getColor(index) + '14', color: getColor(index) }">
                  <v-icon :color="getColor(index)" size="22">{{ module.icon || 'mdi-cube-outline' }}</v-icon>
                </div>
                <div class="mod-card__info">
                  <h3 class="mod-card__name">
                    {{ module.name }}
                    <span v-if="module.beta" class="mod-card__beta">Beta</span>
                  </h3>
                </div>
              </div>
              <div class="mod-card__bottom">
                <span v-if="!module.disabled" class="mod-card__status mod-card__status--pill mod-card__status--active">
                  <span class="mod-status-dot"></span>
                  Active
                </span>
                <span v-else class="mod-card__status mod-card__status--pill mod-card__status--disabled">
                  Disabled
                </span>
                <v-icon v-if="!module.disabled" size="16" class="mod-card__go" color="#c0c7cf">mdi-arrow-right</v-icon>
              </div>
            </div>
          </div>
        </router-link>

        <!-- Plain text -->
        <div v-else :key="'txt-' + index" class="mod-card-link">
          <div class="mod-card">
            <div class="mod-card__content">
              <div class="mod-card__top">
                <div class="mod-card__icon" :style="{ background: getColor(index) + '14', color: getColor(index) }">
                  <v-icon :color="getColor(index)" size="22">{{ module.icon || 'mdi-cube-outline' }}</v-icon>
                </div>
                <div class="mod-card__info">
                  <h3 class="mod-card__name">{{ module.name }}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    modules: {
      type: Array,
    },
  },
  data: () => ({
    colors: [
      '#0ea5e9',
      '#8b5cf6',
      '#f59e0b',
      '#10b981',
      '#ef4444',
      '#ec4899',
      '#6366f1',
      '#14b8a6',
      '#f97316',
      '#06b6d4',
    ],
  }),
  methods: {
    getColor(index) {
      return this.colors[index % this.colors.length];
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   DARK MODE
   ----------------------------------------------------------------------------
   Hand-authored — do NOT regenerate this from the light rules below. Same
   system as the console dashboard (see the DARK MODE block in
   src/views/console/PageConsoleDashboard.vue): the surface ramp does the
   separating, the edge is --app-border rather than the light theme's
   near-white hairline, and depth is shadow + a 1px inner top highlight.

   Colours are token references only. No literal hexes.
   ============================================================================ */

.dark-mode .mod-empty {
  color: var(--app-text-faint);
}

.dark-mode .mod-card {
  background: var(--app-card);
  border-color: var(--app-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 4px 14px var(--app-shadow-mid);
}

.dark-mode .mod-card:hover {
  background: var(--app-elevated);
  border-color: var(--app-accent-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 12px 28px var(--app-shadow);
}

.dark-mode .mod-card-link--disabled .mod-card {
  opacity: 0.42;
}

/* The tile's fill is written inline as the module colour + '14' (8% alpha),
   tuned against white — on charcoal it all but vanishes and every module ends
   up the same grey. The template also writes that colour as the tile's own
   `color`, so currentColor here repaints the fill at a workable strength
   without the component needing to know which theme is active. */
.dark-mode .mod-card__icon {
  background: transparent !important;
  position: relative;
}

.dark-mode .mod-card__icon::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0.18;
}

.dark-mode .mod-card__icon .v-icon {
  position: relative;
}

.dark-mode .mod-card__name {
  color: var(--app-text);
}

.dark-mode .mod-card__desc {
  color: var(--app-text-faint);
}

.dark-mode .mod-card__beta {
  color: var(--app-warning-fg);
  background: var(--app-warning-bg);
  border-color: transparent;
}

.dark-mode .mod-card__status--active {
  color: var(--app-success-fg);
  background: var(--app-success-bg);
}

.dark-mode .mod-status-dot {
  background: var(--app-success);
}

.dark-mode .mod-card__status--disabled {
  color: var(--app-text-faint);
  background: var(--app-elevated);
}

.dark-mode .mod-card__status--external {
  color: var(--app-text-muted);
  background: var(--app-elevated);
}

/* Written inline as a Vuetify `color` prop, so it takes !important to beat. */
.dark-mode .mod-card__go {
  color: var(--app-text-faint) !important;
}

.dark-mode .mod-card:hover .mod-card__go {
  color: var(--app-accent) !important;
}

/* ===== end DARK MODE ===== */

.mod-empty {
  color: #94a3b8;
  font-size: 0.9rem;
  font-family: 'Lato', sans-serif;
}

.mod-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 18px;
}

.mod-divider {
  grid-column: 1 / -1;
  height: 0;
}

/* Card link */
.mod-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.mod-card-link--hidden {
  display: none;
}

.mod-card-link--disabled {
  pointer-events: none;
}

.mod-card-link--disabled .mod-card {
  opacity: 0.5;
}

/* Card */
/* Same correction as .service-card on the console dashboard: the page behind
   this grid is a frosted sheet now, which composites to about #eef0f3 in light
   mode. A half-alpha rim over that lands near #f0f2f5 and a 0.04 shadow barely
   registers, so the cards read as faint rectangles rather than as objects. Full
   -strength rim plus the shared lift is what separates a card from a sheet. */
.mod-card {
  background: var(--app-card);
  border: 1px solid var(--app-border);
  border-radius: 14px;
  box-shadow: var(--glass-lift);
  display: flex;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

/* Deepen the lift and darken the rim; tinting the fill toward --app-elevated
   would walk it back into the sheet's own value. */
.mod-card:hover {
  box-shadow: var(--glass-lift-hover);
  border-color: var(--app-border-strong);
  transform: translateY(-3px);
}

/* Content */
.mod-card__content {
  flex: 1;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Top */
.mod-card__top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mod-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.mod-card:hover .mod-card__icon {
  transform: scale(1.05);
}

.mod-card__info {
  flex: 1;
  min-width: 0;
}

.mod-card__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  font-family: 'Lato', sans-serif;
}

.mod-card__desc {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 4px 0 0;
}

.mod-card__beta {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 7px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #f59e0b;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  vertical-align: middle;
}

/* Bottom */
.mod-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.mod-card__status {
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.mod-card__status--pill {
  padding: 4px 12px;
  border-radius: 20px;
}

.mod-card__status--active {
  color: #10b981;
  background: #ecfdf5;
}

.mod-card__status--disabled {
  color: #94a3b8;
  background: #f8fafc;
}

.mod-card__status--external {
  color: #64748b;
  background: #f8fafc;
}

.mod-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
}

/* Go arrow */
.mod-card__go {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.mod-card:hover .mod-card__go {
  color: #0ea5e9 !important;
  transform: translateX(4px);
}

/* Mobile */
@media (max-width: 599px) {
  .mod-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .mod-card__content {
    padding: 14px 16px;
    gap: 10px;
  }

  .mod-card__icon {
    width: 36px;
    height: 36px;
  }

  .mod-card__name {
    font-size: 0.88rem;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .mod-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }
}
</style>
